import { Modal, useModals } from 'almanac-ui/Modal'
import { useState, useRef, useMemo } from 'react'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { StripePaymentForm, StripePaymentFormRef } from 'components/StripePaymentForm'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useDeleteExternalIntegration } from 'hooks/queries/externalIntegrations/useDeleteExternalIntegration'
import { useQueryClient } from 'react-query'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { PLATFORM_DISPLAY_NAMES } from 'utils/platformDisplayNames'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import Styles from './PurchaseAdditionalAccountModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'

interface Props {
  integration: ExternalIntegration
  onClose: () => void
  onPurchase?: () => void
}

export const PurchaseAdditionalAccountModal = ({ integration, onClose, onPurchase }: Props) => {
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(workspaceId)
  const { data: currentPlan } = useCurrentPlan()
  const { autopilotAddOns } = useRootStore().tierUsageStore
  const stripeFormRef = useRef<StripePaymentFormRef>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const queryClient = useQueryClient()

  const integrationName = PLATFORM_DISPLAY_NAMES[integration.provider]
  const { name, avatar, email, username } = extractProfileInfo(integration)
  const subName = email || username

  const pricing = useMemo(() => {
    const currentPrice = currentPlan?.prices?.[0]
    const intervalCount = currentPrice?.recurring?.interval_count || 1

    const additionalAccountAddon = autopilotAddOns.find(
      (addon) => addon.key === PaymentPlanKey.BlazeAutopilotExtraAccounts,
    )
    const additionalAccountPrice = additionalAccountAddon?.prices.find(
      (p) => p.recurring?.interval_count === intervalCount,
    )

    const monthlyPrice = additionalAccountPrice
      ? Math.round(additionalAccountPrice.unit_amount / 100 / intervalCount)
      : null

    return {
      priceId: additionalAccountPrice?.id || null,
      monthlyPrice,
    }
  }, [autopilotAddOns, currentPlan])

  const { mutateAsync: deleteIntegration } = useDeleteExternalIntegration(integration.id, {
    onSuccess: () => {
      showToastMessage(`Successfully disconnected ${integrationName} account!`, 'success')
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      closeModal()
      onClose()
    },
    onError: () => {
      showToastMessage(`Unable to disconnect ${integrationName} account`, 'error')
    },
  })

  const handleDisconnect = async () => {
    await deleteIntegration()
  }

  const handlePaymentMethodCreated = async (paymentMethodId?: string) => {
    if (!workspaceId || !pricing.priceId) {
      showToastMessage('Unable to purchase account - pricing information not available', 'error')
      setIsProcessing(false)
      return
    }

    setIsProcessing(true)

    try {
      await WorkspaceClient.purchaseAdditionalAccount(workspaceId, pricing.priceId, paymentMethodId, integration.id)

      showToastMessage(`Successfully purchased ${integrationName} account!`, 'success')
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      closeModal()
      onClose()
      onPurchase?.()
      setIsProcessing(false)
    } catch (error) {
      setIsProcessing(false)
      showToastMessage('Failed to purchase account', 'error')
    }
  }

  const handleError = (error: string) => {
    showToastMessage(error, 'error')
  }

  const handlePurchase = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (stripeFormRef.current) {
      await stripeFormRef.current.submitPayment()
    }
  }

  const canPurchase = !!pricing.priceId && !!clientSecret && !isLoading

  return (
    <Modal.Root
      size="sm"
      onPressOutside={() => {}}
      onClose={onClose}
      aria-label={`Purchase extra ${integrationName} Account`}
    >
      <Modal.Header
        variant="hero"
        heroImage="https://res.cloudinary.com/almanac/image/upload/v1755871734/blaze_assets/65cfcb6ed487608453ac773ecee3a53be5e327f9_exitid.png"
        heroImageAlt=""
        title={`New ${integrationName} account connected!`}
        headingLevel={1}
      />
      <form onSubmit={handlePurchase}>
        <Modal.Content>
          <div className={Styles.content}>
            <div className={Styles.accountSection}>
              <Heading level={5}>Purchase extra {integrationName} Account</Heading>
              <div className={Styles.accountInfo}>
                <Avatar size={40} label={getInitials(name)} url={avatar || Silhouette} />
                <div className={Styles.accountDetails}>
                  <Text variant="smallList" color="var(--dark-80)">
                    {name}
                  </Text>
                  {subName && (
                    <Text variant="metadata" color="var(--dark-40)">
                      {subName}
                    </Text>
                  )}
                </div>
                <Text color="var(--dark-80)">
                  {pricing.monthlyPrice !== null ? `$${pricing.monthlyPrice} per month` : 'Price unavailable'}
                </Text>
              </div>
            </div>

            {!pricing.priceId && (
              <div style={{ padding: '12px 0' }}>
                <Text variant="secondary" color="var(--red-50)">
                  Unable to load pricing. Please refresh and try again.
                </Text>
              </div>
            )}

            {clientSecret && pricing.priceId && (
              <div className={Styles.paymentSection}>
                <StripePaymentForm
                  ref={stripeFormRef}
                  clientSecret={clientSecret}
                  onPaymentMethodCreated={handlePaymentMethodCreated}
                  onError={handleError}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                />
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" onPress={handleDisconnect} isDisabled={isProcessing}>
              Disconnect Account
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button type="submit" variant="primary" isDisabled={isProcessing || !canPurchase}>
              {isProcessing ? 'Processing...' : 'Purchase Account'}
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </form>
    </Modal.Root>
  )
}
